import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  Button,
  Collapse,
  Typography,
  Chip,
  Stack,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  EventAvailable as HoldIcon,
  Warning as ChallengeIcon,
  CheckCircle as ConfirmedIcon,
  Event as CompletedIcon,
  AccountBalance as SettledIcon,
  Cancel as CancelledIcon,
} from '@mui/icons-material';
import type { Show, HoldList } from 'api-client/models';
import { formatDateShort } from '@/utils/dateUtils';

interface ShowWorkflowTrackerProps {
  show: Show;
  holds?: HoldList[];
}

interface WorkflowStep {
  label: string;
  description: string;
  icon: React.ReactNode;
  status: 'completed' | 'active' | 'pending' | 'cancelled';
}

export const ShowWorkflowTracker: React.FC<ShowWorkflowTrackerProps> = ({
  show,
  holds = [],
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [expanded, setExpanded] = useState(false);

  const holdCount = holds.length;
  const hasChallengedHolds = holds.some((h) => h.status === 'challenged');
  const hasInitiatedChallenges = holds.some((h) => h.hasInitiatedChallenge);

  // Determine current step based on show status
  const getCurrentStep = (): number => {
    if (show.isSettled) return 4; // Settled
    if (show.status === 'cancelled') return -1; // Cancelled
    if (show.status === 'completed') return 3; // Completed
    if (show.status === 'confirmed') return 2; // Confirmed
    if (hasChallengedHolds || hasInitiatedChallenges) return 1; // Challenges initiated or received
    if (holdCount > 0) return 0; // Hold pending
    return 0; // Default to hold pending
  };

  const currentStep = getCurrentStep();
  const isCancelled = show.status === 'cancelled';

  const getStepStatus = (
    stepIndex: number,
  ): 'completed' | 'active' | 'pending' | 'cancelled' => {
    if (isCancelled) return 'cancelled';
    if (stepIndex < currentStep) return 'completed';
    if (stepIndex === currentStep) return 'active';
    return 'pending';
  };

  const steps: WorkflowStep[] = [
    {
      label: 'Hold Pending',
      description: `${holdCount} hold${holdCount !== 1 ? 's' : ''} placed, awaiting confirmation`,
      icon: <HoldIcon />,
      status: getStepStatus(0),
    },
    {
      label: 'Challenges Initiated',
      description: 'Challenge cascade initiated, awaiting responses',
      icon: <ChallengeIcon />,
      status: getStepStatus(1),
    },
    {
      label: 'Confirmed',
      description: 'Date and venue confirmed, show is locked in',
      icon: <ConfirmedIcon />,
      status: getStepStatus(2),
    },
    {
      label: 'Completed',
      description: 'Show has taken place, awaiting settlement',
      icon: <CompletedIcon />,
      status: getStepStatus(3),
    },
    {
      label: 'Settled',
      description: 'All financial matters resolved',
      icon: <SettledIcon />,
      status: getStepStatus(4),
    },
  ];

  const getStepColor = (status: string) => {
    switch (status) {
      case 'completed':
        return theme.palette.statusText?.ready ?? theme.palette.success.main;
      case 'active':
        return theme.palette.text.primary;
      case 'cancelled':
        return theme.palette.statusText?.attention ?? theme.palette.error.main;
      case 'pending':
      default:
        return theme.palette.text.secondary;
    }
  };

  const getStepIcon = (step: WorkflowStep): React.ReactNode => {
    const color = getStepColor(step.status);

    // Return a new icon element with the appropriate color
    if (step.label === 'Hold Pending') {
      return <HoldIcon sx={{ color, fontSize: 28 }} />;
    } else if (step.label === 'Challenges Initiated') {
      return <ChallengeIcon sx={{ color, fontSize: 28 }} />;
    } else if (step.label === 'Confirmed') {
      return <ConfirmedIcon sx={{ color, fontSize: 28 }} />;
    } else if (step.label === 'Completed') {
      return <CompletedIcon sx={{ color, fontSize: 28 }} />;
    } else if (step.label === 'Settled') {
      return <SettledIcon sx={{ color, fontSize: 28 }} />;
    }

    return step.icon;
  };

  if (isCancelled) {
    return (
      <Card sx={{ mb: 3, bgcolor: 'error.light' }}>
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="center">
            <CancelledIcon
              sx={{ fontSize: 40, color: 'statusText.attention' }}
            />
            <Box>
              <Typography
                variant="h6"
                color="statusText.attention"
                gutterBottom
              >
                Show Cancelled
              </Typography>
              <Typography variant="body2" color="statusText.attention">
                This show has been cancelled and will not proceed through the
                normal workflow.
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Stack
          direction="row" useFlexGap sx={{ flexWrap: 'wrap', gap: 1 }}
          spacing={1}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          mb={1}
        >
          <Typography variant="h6">Show Lifecycle</Typography>
          <Chip
            label={steps[currentStep]?.label || 'Unknown'}
            color={
              currentStep === 4
                ? 'success'
                : currentStep >= 2
                  ? 'primary'
                  : 'default'
            }
            icon={getStepIcon(steps[currentStep]) as React.ReactElement}
          />
        </Stack>

        {isMobile && (
          <>
            <Typography variant="body2" color="text.secondary">{steps[currentStep].description}</Typography>
            <Button onClick={() => setExpanded(!expanded)} aria-expanded={expanded} aria-controls="show-lifecycle-steps">
              {expanded ? 'Hide lifecycle steps' : 'View lifecycle steps'}
            </Button>
          </>
        )}
        <Collapse in={!isMobile || expanded} id="show-lifecycle-steps" unmountOnExit>
          <Stepper
            activeStep={currentStep}
            alternativeLabel={!isMobile}
            orientation={isMobile ? 'vertical' : 'horizontal'}
            sx={{
              '& .MuiStepLabel-root .Mui-completed': {
                color: 'statusText.ready',
              },
              '& .MuiStepLabel-root .Mui-active': {
                color: 'readable.primary',
              },
            }}
          >
            {steps.map((step) => (
              <Step key={step.label} completed={step.status === 'completed'} sx={{ px: isMobile ? 0 : 0.5 }}>
                <StepLabel
                  icon={getStepIcon(step)}
                  optional={
                    isMobile && (
                      <Typography variant="caption" color="text.secondary">
                        {step.description}
                      </Typography>
                    )
                  }
                >
                  <Typography
                    variant="body2"
                    fontWeight={step.status === 'active' ? 'bold' : 'normal'}
                    color={
                      step.status === 'active'
                        ? 'readable.primary'
                        : step.status === 'completed'
                          ? 'statusText.ready'
                          : 'text.secondary'
                    }
                  >
                    {step.label}
                  </Typography>
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Collapse>

        {!isMobile && (
          <Box mt={1} px={{ xs: 0, sm: 2 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              <strong>Current Status:</strong>{' '}
              {steps[currentStep]?.description || 'Unknown status'}
            </Typography>
          </Box>
        )}

        {/* Additional context for holds */}
        {currentStep === 0 && holdCount > 0 && (
          <Box mt={1} px={{ xs: 0, sm: 2 }}>
            <Typography variant="caption" color="text.primary" display="block">
              💡 <strong>Next Step:</strong> Select hold dates below to confirm
              or challenge positions
            </Typography>
          </Box>
        )}

        {/* Additional context for challenges */}
        {currentStep === 1 && (
          <Box mt={1} px={{ xs: 0, sm: 2 }}>
            <Typography
              variant="caption"
              color="statusText.inFlight"
              display="block"
            >
              ⚠️ <strong>Active Challenges:</strong>{' '}
              {isMobile ? 'Respond using the challenged dates below before their listed deadline.' : 'Challenged holds have 24 hours to respond'}
            </Typography>
          </Box>
        )}

        {/* Additional context for confirmed shows */}
        {currentStep === 2 && (show.showDate || isMobile) && (
          <Box mt={1} px={{ xs: 0, sm: 2 }}>
            <Typography variant="caption" color="text.primary" display="block">
              💡 <strong>Next Step:</strong>{' '}
              {isMobile ? 'Select a date below to update timing, ticketing, and financial details.' :
                <>Show will automatically move to "Completed" after the show date ({formatDateShort(show.showDate!)})</>}
            </Typography>
          </Box>
        )}

        {/* Additional context for completed shows */}
        {currentStep === 3 && (
          <Box mt={1} px={{ xs: 0, sm: 2 }}>
            <Typography variant="caption" color="text.primary" display="block">
              💡 <strong>Next Step:</strong> Complete financial settlement to
              move to "Settled" status
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
